/**
 * One meeting on the Meet page: when, what, who hosts, its state, and the
 * actions that state allows. A live or upcoming meeting joins and copies its
 * link; a finished one opens its transcript and, when it was recorded, plays
 * the recording in a pop-up. The host can cancel what has not
 * started and close what is running.
 */
import { Captions, Check, Copy, Disc, Link2, Pencil, Play, Video } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import type { VideoMeetingSummary } from '../../lib/meetApi';
import { MeetRecordingPlayer } from './MeetRecordingPlayer';
import { LiveBadge } from '../teamChat/VoiceChannelRow';
import { useCallDuration } from '../teamChat/VoiceCallHistory';

export function meetRoomPath(code: string): string {
  return `/meet/${encodeURIComponent(code)}`;
}

export function meetHistoryPath(id: string): string {
  return `/meet/history/${encodeURIComponent(id)}`;
}

// The Meet page groups rows under a day heading, so the row itself only needs
// the clock.
const whenFormat = new Intl.DateTimeFormat('ro-RO', {
  hour: '2-digit',
  minute: '2-digit',
});

/**
 * Copies `text` and confirms with a check mark for a moment. `label` is the
 * idle caption; the confirmation is always "Copiat".
 */
export function CopyTextButton({
  text,
  label,
  compact = false,
}: {
  text: string;
  label: string;
  compact?: boolean;
}) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked; the details are visible on the page anyway.
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={label}
      className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
    >
      {copied ? <Check size={15} className="text-emerald-600" /> : <Copy size={15} />}
      {!compact && (copied ? t('meet.copied') : label)}
    </button>
  );
}

export function CopyLinkButton({ url, compact = false }: { url: string; compact?: boolean }) {
  const { t } = useI18n();
  return <CopyTextButton text={url} label={t('meet.copyLink')} compact={compact} />;
}

export function MeetingCard({
  meeting,
  isHost,
  onEdit,
  onCancel,
  onEnd,
}: {
  meeting: VideoMeetingSummary;
  isHost: boolean;
  onEdit?: (meeting: VideoMeetingSummary) => void;
  onCancel?: (id: string) => void;
  onEnd?: (id: string) => void;
}) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const duration = useCallDuration();
  const live = meeting.status === 'LIVE';
  const upcoming = meeting.status === 'SCHEDULED';
  const past = meeting.status === 'ENDED' || meeting.status === 'CANCELLED';
  const recorded = past && meeting.recordingCount > 0;
  const [playing, setPlaying] = useState(false);

  return (
    <li className="flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-foreground/25 hover:bg-accent/40 sm:flex-row sm:items-center">
      <span
        className={[
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          live ? 'bg-emerald-500/15 text-emerald-600' : 'bg-accent text-muted-foreground',
        ].join(' ')}
      >
        <Video size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          {past ? (
            <Link
              to={meetHistoryPath(meeting.id)}
              className="truncate text-sm font-semibold text-foreground hover:underline"
            >
              {meeting.title}
            </Link>
          ) : (
            <span className="truncate text-sm font-semibold text-foreground">{meeting.title}</span>
          )}
          {live && <LiveBadge />}
          {recorded && (
            <span className="inline-flex items-center gap-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[11px] font-medium text-red-500">
              <Disc size={11} />
              {t('meet.recordings.badge')}
            </span>
          )}
          {meeting.status === 'CANCELLED' && (
            <span className="rounded bg-accent px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
              {t('meet.status.cancelled')}
            </span>
          )}
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {whenFormat.format(new Date(meeting.scheduledAt))}
          {' · '}
          {past && meeting.durationMs > 0
            ? duration(meeting.durationMs)
            : `${meeting.durationMinutes} min`}
          {' · '}
          {t('meet.hostedBy', { name: meeting.hostName })}
          {meeting.clientName && ` · ${meeting.clientName}`}
          {past && (
            <>
              {' · '}
              {meeting.segmentCount > 0 ? (
                <span className="inline-flex items-center gap-1">
                  <Captions size={12} />
                  {t('teamChat.voiceCallSegments', { count: meeting.segmentCount })}
                </span>
              ) : (
                t('teamChat.voiceCallNoTranscript')
              )}
            </>
          )}
        </p>
      </div>
      <div className="flex shrink-0 flex-wrap items-center gap-1.5">
        {(live || upcoming) && (
          <>
            <CopyLinkButton url={meeting.joinUrl} compact />
            {isHost && onEdit && (
              <button
                type="button"
                onClick={() => onEdit(meeting)}
                title={t('meet.form.editTitle')}
                aria-label={t('meet.form.editTitle')}
                className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Pencil size={15} />
              </button>
            )}
            <button
              type="button"
              onClick={() => navigate(meetRoomPath(meeting.code))}
              className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-fixed-white hover:bg-emerald-700"
            >
              <Link2 size={15} />
              {live ? t('meet.join') : t('meet.open')}
            </button>
            {isHost && upcoming && onCancel && (
              <button
                type="button"
                onClick={() => onCancel(meeting.id)}
                className="rounded-md px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-red-600"
              >
                {t('meet.cancel')}
              </button>
            )}
            {isHost && live && onEnd && (
              <button
                type="button"
                onClick={() => onEnd(meeting.id)}
                className="rounded-md px-2.5 py-1.5 text-sm font-medium text-red-600 hover:bg-red-500/10"
              >
                {t('meet.controls.endForAll')}
              </button>
            )}
          </>
        )}
        {recorded && (
          <button
            type="button"
            onClick={() => setPlaying(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Play size={15} />
            {t('meet.recordings.watch')}
          </button>
        )}
        {past && (
          <Link
            to={meetHistoryPath(meeting.id)}
            className="rounded-md border border-border px-2.5 py-1.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {t('meet.viewTranscript')}
          </Link>
        )}
      </div>
      {playing && (
        <MeetRecordingPlayer
          meetingId={meeting.id}
          title={meeting.title}
          onClose={() => setPlaying(false)}
        />
      )}
    </li>
  );
}
